import { Box, Typography } from '@mui/material';
import DeformIcon from '~/assets/tool-icons/deform.svg?react';
import LineDeformIcon from '~/assets/tool-icons/line_deform.svg?react';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import {
    AdaptiveBrushIcon,
    BrushIcon,
    EraseIcon,
    LineIcon,
    TransformIcon,
} from '~/assets/workspace-icons';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import ToolPropertiesTray from '~/components/toolbar/shared/ToolPropertiesTray';
import { useStructureToolGating } from '~/components/toolbar/shared/useStructureToolGating';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { useContourAutoSuggestionController } from '~/hooks/useContourAutoSuggestionController';

import {
    brushOptionsState,
    lineOptionsState,
    selectedToolState,
    contourAutoSuggestionEnabledState,
} from '~/state/atoms/toolsAtoms';
import BrushToolOptions from './BrushToolOptions';
import DeformToolOptions from './DeformToolOptions';
import LineDeformToolOptions from './LineDeformToolOptions';
import LineToolOptions from './LineToolOptions';
import TransformToolOptions from './TransformToolOptions';

export default function ContouringTools() {
    const intl = useIntl();
    const [tool, setTool] = useRecoilState(selectedToolState);
    const {
        selectedStructureSet,
        selectedRoiNumber,
        selectedRoi,
        isPointRoi,
        isTilted,
        isSelectionApproved,
        isBulkSelectionActive,
        hasSelectedRoi,
        selectedRoiContourEditDisabled,
    } = useStructureToolGating();
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);
    const [lineOptions, setLineOptions] = useRecoilState(lineOptionsState);
    const setAutoSuggestEnabled = useSetRecoilState(
        contourAutoSuggestionEnabledState
    );
    const [validationError, setValidationError] = useState<string | null>(null);

    useContourAutoSuggestionController();

    const noRoiSelected = !hasSelectedRoi;

    const isContouringToolsDisabled = selectedRoiContourEditDisabled;

    const isMoveTransformDisabled =
        noRoiSelected ||
        isTilted ||
        isSelectionApproved ||
        isBulkSelectionActive;

    /**
     * When a contouring tool is disabled, return a short reason
     * that can be appended to the tooltip so the user knows why.
     * Returns undefined when the tool isn't disabled (caller falls
     * back to the plain tool name).
     */
    const disabledReason = useMemo<string | undefined>(() => {
        if (isBulkSelectionActive) {
            return intl.formatMessage({ id: 'tool.disabled.bulkSelection' });
        }
        if (isSelectionApproved) {
            return intl.formatMessage({ id: 'tool.disabled.approved' });
        }
        if (isTilted) {
            return intl.formatMessage({ id: 'tool.disabled.oblique' });
        }
        if (isPointRoi) {
            return intl.formatMessage({ id: 'tool.disabled.pointRoi' });
        }
        if (noRoiSelected) {
            return intl.formatMessage({ id: 'tool.disabled.noRoi' });
        }
        return undefined;
    }, [
        intl,
        isSelectionApproved,
        isTilted,
        isPointRoi,
        noRoiSelected,
        isBulkSelectionActive,
    ]);

    const tooltipFor = (toolName: string) =>
        disabledReason
            ? intl.formatMessage(
                  { id: 'tool.disabledTooltip' },
                  { toolName, reason: disabledReason }
              )
            : toolName;

    const isStaticBrushSelected =
        tool === 'brush' &&
        brushOptions.mode === 'static' &&
        !brushOptions.erase;
    const isAdaptiveBrushSelected =
        tool === 'brush' &&
        brushOptions.mode === 'smart' &&
        !brushOptions.erase;
    const isLineDrawSelected = tool === 'line' && lineOptions.mode === 'draw';
    // Highlight follows tool selection (draw or erase); the "Erase" toggle is
    // the mode indicator. Click handling keys off draw mode via isLineDrawSelected.
    const isLineSelected = tool === 'line';
    const isEraseSelected = tool === 'brush' && brushOptions.erase;
    const isTransformSelected = tool === 'transform' || tool === 'poi-move';
    const isLineDeformSelected = tool === 'lineDeform';

    useEffect(() => {
        if (
            selectedRoiNumber === -1 ||
            isSelectionApproved ||
            isBulkSelectionActive
        ) {
            setTool('pan');
            return;
        }

        setTool((prev) => {
            if (isPointRoi) {
                if (prev === 'transform' || prev === 'poi-move') {
                    return 'poi-move';
                }
                return 'pan';
            } else {
                if (prev === 'poi-move') {
                    return 'transform';
                }
            }
            return prev;
        });
    }, [
        selectedRoiNumber,
        isPointRoi,
        isSelectionApproved,
        isBulkSelectionActive,
        setTool,
    ]);

    useEffect(() => {
        if (validationError) {
            enqueueSnackbar(validationError, { variant: 'error' });
            setValidationError(null);
        }
    }, [validationError]);

    useEffect(() => {
        // The auto-suggest toggle persists across tool switches —
        // turning it on while in brush mode keeps it on when the
        // user switches to line / line-deform / etc. so they don't
        // have to re-enable the feature each time. We only force it
        // off when contouring as a whole is unavailable for the
        // current selection (no ROI, point ROI, tilted dataset, or
        // approved structure), since the controller has nothing to
        // operate on in those states.
        if (isContouringToolsDisabled) {
            setAutoSuggestEnabled(false);
        }
    }, [isContouringToolsDisabled, setAutoSuggestEnabled]);

    const validateBrush = (): boolean => {
        if (isTilted) {
            setValidationError(
                'Brush tool is not available for tilted datasets'
            );
            return false;
        }
        return true;
    };

    const handleStaticBrushSelect = () => {
        if (isStaticBrushSelected) {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'static',
            erase: false,
        }));
    };

    const handleAdaptiveBrushSelect = () => {
        if (isAdaptiveBrushSelected) {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'smart',
            erase: false,
        }));
    };

    const handleLineSelect = () => {
        if (isLineDrawSelected) {
            setTool('pan');
            return;
        }
        if (isTilted) {
            setValidationError(
                'Line draw tool is not available for tilted datasets'
            );
            return;
        }
        setTool('line');
        setLineOptions((options) => ({ ...options, mode: 'draw' }));
    };

    const handleEraseSelect = () => {
        if (isEraseSelected) {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'static', // Erase mode uses the same brush behavior as static, just with the erase flag on
            erase: true,
        }));
    };

    const handleTransformSelect = useCallback(() => {
        if (isTransformSelected) {
            setTool('pan');
            return;
        }
        setTool(isPointRoi ? 'poi-move' : 'transform');
    }, [setTool, isPointRoi, isTransformSelected]);

    const handleLineDeformSelect = () => {
        if (isLineDeformSelected) {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('lineDeform');
    };

    const handleDeformSelect = () => {
        if (tool === 'deform') {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('deform');
    };

    const isSelectedSupport =
        selectedRoi?.interpretedType === RoiInterpretedType.SUPPORT;
    const hasSiblingSupport = useMemo(() => {
        if (!isSelectedSupport || !selectedStructureSet) {
            return false;
        }
        return selectedStructureSet.roiArray.some(
            (r) =>
                r.roiNumber !== selectedRoiNumber &&
                r.interpretedType === RoiInterpretedType.SUPPORT
        );
    }, [isSelectedSupport, selectedStructureSet, selectedRoiNumber]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                }}
            >
                <ToolbarButton
                    icon={<BrushIcon />}
                    tooltip={tooltipFor('Static Brush')}
                    tooltipPlacement="bottom"
                    selected={isStaticBrushSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleStaticBrushSelect}
                    data-testid="tool-static-brush"
                />

                <ToolbarButton
                    icon={<AdaptiveBrushIcon />}
                    tooltip={tooltipFor('Adaptive Brush')}
                    tooltipPlacement="bottom"
                    selected={isAdaptiveBrushSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleAdaptiveBrushSelect}
                    data-testid="tool-adaptive-brush"
                />

                <ToolbarButton
                    icon={<LineIcon />}
                    tooltip={tooltipFor('Line Draw')}
                    tooltipPlacement="bottom"
                    selected={isLineSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleLineSelect}
                    data-testid="tool-line-draw"
                />

                <ToolbarButton
                    icon={<EraseIcon />}
                    tooltip={tooltipFor('Erase')}
                    tooltipPlacement="bottom"
                    selected={isEraseSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleEraseSelect}
                    data-testid="tool-erase"
                />
                <ToolbarButton
                    icon={<TransformIcon />}
                    tooltip={
                        isMoveTransformDisabled && disabledReason ? (
                            tooltipFor(
                                isPointRoi ? 'Move POI' : 'Transform Structure'
                            )
                        ) : isPointRoi ? (
                            'Move POI'
                        ) : (
                            <Box>
                                <Typography fontSize={11}>
                                    Transform Structure
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 10,
                                        opacity: 0.7,
                                    }}
                                >
                                    drag to move, structure edge to scale
                                </Typography>
                            </Box>
                        )
                    }
                    selected={isTransformSelected}
                    disabled={isMoveTransformDisabled}
                    onClick={handleTransformSelect}
                    tooltipPlacement="bottom"
                    data-testid="tool-transform"
                />
                <ToolbarButton
                    icon={<LineDeformIcon />}
                    tooltip={tooltipFor('Line deform')}
                    tooltipPlacement="bottom"
                    selected={isLineDeformSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleLineDeformSelect}
                    data-testid="tool-line-deform"
                />
                {false && (
                    <ToolbarButton
                        icon={<DeformIcon />}
                        tooltip="Deform"
                        tooltipPlacement="bottom"
                        selected={tool === 'deform'}
                        disabled={isContouringToolsDisabled}
                        onClick={handleDeformSelect}
                    />
                )}
            </Box>
            {tool === 'brush' && (
                <ToolPropertiesTray>
                    <BrushToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'line' && (
                <ToolPropertiesTray>
                    <LineToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'lineDeform' && (
                <ToolPropertiesTray>
                    <LineDeformToolOptions />
                </ToolPropertiesTray>
            )}
            {false && tool === 'deform' && (
                <ToolPropertiesTray>
                    <DeformToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'transform' && isSelectedSupport && hasSiblingSupport && (
                <ToolPropertiesTray>
                    <TransformToolOptions />
                </ToolPropertiesTray>
            )}
        </>
    );
}
